// libs
import * as React from 'react'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'

// interfaces
import { IWindowJsEnv } from 'src/interfaces/'

// constants
import { CLASS_PREFIX } from 'src/constants'
import { POST_DETAILS, PostDetailsObjType } from 'src/utils/reporting/events_tracking/events_tracking'
// classes / components
import PostDetails from 'src/components/post/detail/post_detail'

// helpers
import { ContentItemFragmentFragment, ContentItemQuery } from '@lopo-tech/lopo-api'
import { FC, useCallback, useEffect } from 'react'
import { closeTopbarPopupMenu } from 'src/actions/app-state/app-state'
import { useChangeAppLocation } from 'src/actions/app-state/app-state_hooks'
import { callToActionWithFlag } from 'src/actions/onboarding_cta/onboarding_cta'
import { ITrackingProps, withTrackerProps } from 'src/high_order_components/with_tracker_props'
import { textResources } from 'src/lang/de'
import { useGetAppLocationShape } from 'src/reducers/app-state/app-state_hooks'
import { isPrerenderIoAgent } from 'src/utils/user_agent/user_agent'
import { WithSurveyProps } from '../../plugins/survey/hoc/model'

interface IOwnProps {
  contentItem: ContentItemQuery['contentItem']
  loading: boolean
}

type IProps = IOwnProps & ITrackingProps<PostDetailsObjType> & WithSurveyProps;

const _window = window as unknown as IWindowJsEnv
const PREMIUM_CONTENT_TIMEOUT = _window.js_env.userAnnoyer.timerOnDetailPage * 1000

const cls = CLASS_PREFIX + 'post__detail-container'

const shouldActivateCTA = (post ?: ContentItemFragmentFragment | null): boolean => {
  // On premium and fenced post, we won't trigger a CTA after certain seconds. See #4784
  return !!post && post.canSeeFullContent.value
}

const PostDetailsContainer: FC<IProps> = ({
  contentItem,
  loading,
  tracking
}) => {
  const history = useHistory()

  const dispatch = useDispatch()

  const locationShape = useGetAppLocationShape()
  const changeAppLocation = useChangeAppLocation()

  const addViewCount = useCallback(async () => {
    if (isPrerenderIoAgent()) {
      return
    }
    if (contentItem) {
      // TODO: Tracking should be reenabled
      // api.post.addView(post.meta.urls.addView)
      //   .catch((error) => reportError('AddView for this post failed:', { error, post }))
    }
  }, [contentItem])

  // TODO: Survey handling
  // useEffect(() => {
  //   if (isSurveyEnabled && post?.survey) {
  //     history.push(FRONTEND_NOT_FOUND_PATH)
  //   }
  // }, [isSurveyEnabled, post?.survey])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [contentItem?.id])

  useEffect(() => {
    if (locationShape || loading || !contentItem) {
      return
    }

    if (contentItem.address.locationShape) {
      changeAppLocation(contentItem.address.locationShape)
    } else {
      reportError('contentItem details page received a contentItem with no position:', contentItem)
    }
  }, [contentItem, locationShape, loading])

  useEffect(() => {
    if (shouldActivateCTA(contentItem)) {
      // @ts-expect-error Action types mismatch
      const timeOutId = window.setTimeout(() => dispatch(callToActionWithFlag(999999)), PREMIUM_CONTENT_TIMEOUT)

      return () => {
        window.clearTimeout(timeOutId)
      }
    }
  }, [callToActionWithFlag, contentItem, PREMIUM_CONTENT_TIMEOUT])

  useEffect(() => {
    // @ts-expect-error Action types mismatch
    dispatch(closeTopbarPopupMenu())
  }, [])

  if (!contentItem) {
    if (loading) {
      return null
    }

    return (
      <div className={cls}>
        <h1>{textResources.shared.pageNotFound}</h1>
      </div>
    )
  }

  return (
    <PostDetails
      addViewCount={addViewCount}
      post={contentItem}
      history={history}
      tracking={tracking}
    />
  )
}

export default withTrackerProps<typeof POST_DETAILS, IOwnProps & WithSurveyProps>(POST_DETAILS)(PostDetailsContainer)
